import React, { useState, useEffect, useRef } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useAppUpdates } from "@/lib/appUpdatesStore";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  LogOut, User, Menu, X, MessageCircle, ShoppingCart, Database, 
  Bell, BookOpen, HelpCircle, LayoutDashboard, Truck, RotateCcw,
  ChevronDown, ChevronRight, Package, FileText, Users, Megaphone,
  ClipboardCheck, AlertTriangle
} from "lucide-react";
import { toast } from "sonner";
import { pollGet, runPollTasks, startPolling } from "@/utils/layoutPolling";
import MobileStickyHeader from "@/components/layout/MobileStickyHeader";
import PullToRefresh from "@/components/layout/PullToRefresh";
import AddressChangePopup from "@/components/gudang/AddressChangePopup";
import { playNotificationSound } from "@/utils/notificationSound";
import useProductConfirmCount from '@/utils/useProductConfirmCount';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Collapsible Menu Group Component
const MenuGroup = ({ icon: Icon, label, children, badge, isExpanded, onToggle, isActive }) => {
  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
          isActive
            ? "bg-blue-50 text-blue-700 font-medium"
            : "text-gray-700 hover:bg-gray-100"
        }`}
      >
        <div className="flex items-center space-x-3">
          <Icon className="w-5 h-5" />
          <span className="font-medium">{label}</span>
        </div>
        <div className="flex items-center space-x-2">
          {badge > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
              {badge}
            </span>
          )}
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
        </div>
      </button>
      {isExpanded && (
        <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-2">
          {children}
        </div>
      )}
    </div>
  );
};

// Menu Item Component
const MenuItem = ({ to, icon: Icon, label, badge, onClick, isActive }) => {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center justify-between px-4 py-2.5 rounded-lg transition-colors ${
        isActive
          ? "bg-blue-100 text-blue-700 font-medium"
          : "text-gray-600 hover:bg-gray-100"
      }`}
    >
      <div className="flex items-center space-x-3">
        {Icon && <Icon className="w-4 h-4" />}
        <span className="text-sm">{label}</span>
      </div>
      {badge > 0 && (
        <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[18px] text-center">
          {badge}
        </span>
      )}
    </Link>
  );
};

// Standalone Menu Item Component
const StandaloneMenuItem = ({ to, icon: Icon, label, badge, onClick, isActive }) => {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
        isActive
          ? "bg-blue-50 text-blue-700 font-medium"
          : "text-gray-700 hover:bg-gray-100"
      }`}
    >
      <div className="flex items-center space-x-3">
        <Icon className="w-5 h-5" />
        <span className="font-medium">{label}</span>
      </div>
      {badge > 0 && (
        <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
          {badge}
        </span>
      )}
    </Link>
  );
};

export default function GudangLayout() {
  const { user, logout } = useAuth();
  const { count: appUpdatesCount } = useAppUpdates();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const productConfirmCount = useProductConfirmCount(true);
  
  // Badge counts
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [unreadAnnouncementsCount, setUnreadAnnouncementsCount] = useState(0);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const prevUnreadNotifRef = useRef(null);
  const [pendingShipmentsCount, setPendingShipmentsCount] = useState(0);
  const [pendingReturnsCount, setPendingReturnsCount] = useState(0);

  // Collapsible state
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try {
      const saved = localStorage.getItem('gudang_sidebar_expanded');
      return saved ? JSON.parse(saved) : { pusatInfo: false, pemesanan: false, chat: false };
    } catch {
      return { pusatInfo: false, pemesanan: false, chat: false };
    }
  });

  // Save expanded state
  useEffect(() => {
    localStorage.setItem('gudang_sidebar_expanded', JSON.stringify(expandedGroups));
  }, [expandedGroups]);

  // Auto-expand based on route
  useEffect(() => {
    const path = location.pathname;
    
    if (path.includes('/announcements') || path.includes('/tutorial') || path.includes('/apa-yang-baru')) {
      setExpandedGroups(prev => ({ ...prev, pusatInfo: true }));
    }
    if (path.includes('/purchase-orders') || path.includes('/master-')) {
      setExpandedGroups(prev => ({ ...prev, pemesanan: true }));
    }
    if (path.includes('/chat') || path.includes('/coordination')) {
      setExpandedGroups(prev => ({ ...prev, chat: true }));
    }
  }, [location.pathname]);

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const closeSidebar = () => setSidebarOpen(false);
  
  // Feb 2026 — Unified chat badge dari /notifications/badge-summary (konsisten
  // across all roles). Existing supply-request unread count di-preserve untuk
  // submenu spesifik.
  const [chatBadgeTotal, setChatBadgeTotal] = useState(0);

  // Fetch counts
  // Okt 2026 (Fase 3) — enam request bersamaan dengan timeout. Penjaga
  // "putaran lama belum selesai" kini ditangani oleh startPolling.
  const fetchCounts = async () => {
    const token = localStorage.getItem('token');
    if (!token) return true;
    const headers = { Authorization: `Bearer ${token}` };

    return runPollTasks([
      // Unified badge-summary → parent Chat badge
      async () => {
        const bs = await pollGet(`${API}/notifications/badge-summary`, { headers });
        setChatBadgeTotal(bs.data?.chat?.total || 0);
      },
      // Fetch unread chat from nakes
      async () => {
        const chatRes = await pollGet(`${API}/supply-requests/discussions/unread-by-nakes`, { headers });
        setUnreadChatCount((chatRes.data || []).length);
      },
      // Fetch unread announcements
      async () => {
        const announcementsRes = await pollGet(`${API}/knowledge-center/announcements/unread-count`, { headers });
        setUnreadAnnouncementsCount(announcementsRes.data.unread_count || 0);
      },
      // Fetch unread notifications
      async () => {
        const notificationsRes = await pollGet(`${API}/notifications/unread-count`, { headers });
        const newUnread = notificationsRes.data.unread_count || 0;
        setUnreadNotificationsCount(newUnread);
        if (prevUnreadNotifRef.current !== null && newUnread > prevUnreadNotifRef.current) {
          playNotificationSound();
        }
        prevUnreadNotifRef.current = newUnread;
      },
      // Fetch pending shipments
      async () => {
        const shipmentsRes = await pollGet(`${API}/supply-requests`, {
          headers,
          params: { status: 'pending' }
        });
        const requests = shipmentsRes.data?.requests || shipmentsRes.data || [];
        const pending = requests.filter(r => ['REQUEST', 'PROCESSED', 'READY_TO_SHIP'].includes(r.status));
        setPendingShipmentsCount(pending.length);
      },
      // Fetch pending returns
      async () => {
        const returnsRes = await pollGet(`${API}/logistic-returns`, { headers });
        const returns = returnsRes.data?.returns || returnsRes.data || [];
        const pending = returns.filter(r => ['REQUESTED', 'SCHEDULED', 'IN_TRANSIT'].includes(r.status));
        setPendingReturnsCount(pending.length);
      },
    ]);
  };

  useEffect(() => {
    // Okt 2026 (Fase 3) — polling 30 detik lewat startPolling: tidak menumpuk,
    // berhenti saat tab tersembunyi atau offline, backoff saat gagal.
    const stopPolling = startPolling(fetchCounts);
    return () => {
      stopPolling();
    };
  }, []);

  // Check active routes
  const isRouteActive = (path) => location.pathname.startsWith(path);
  
  const isPusatInfoActive = isRouteActive('/gudang/announcements') || isRouteActive('/gudang/tutorial') || isRouteActive('/gudang/apa-yang-baru');
  const isPemesananActive = isRouteActive('/gudang/purchase-orders') || isRouteActive('/gudang/master-');
  const isChatActive = isRouteActive('/gudang/chat') || isRouteActive('/gudang/coordination');

  return (
    <div className="min-h-screen bg-gray-50 lg:overflow-hidden lg:h-screen">
      {/* Overlay for mobile/tablet */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar - FIXED on desktop (>=1024px) */}
      <div className={`
        fixed inset-y-0 left-0 z-40
        w-64 bg-white border-r border-gray-200 flex flex-col h-screen
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Menu Items (sidebar header removed — branding now in MobileStickyHeader) */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto pt-14 lg:pt-4">
          {/* Dashboard */}
          <StandaloneMenuItem
            to="/gudang/dashboard"
            icon={LayoutDashboard}
            label="Dashboard"
            onClick={closeSidebar}
            isActive={location.pathname === '/gudang/dashboard'}
          />

          {/* Notifikasi - NEW */}
          <StandaloneMenuItem
            to="/gudang/notifications"
            icon={Bell}
            label="Notifikasi"
            badge={unreadNotificationsCount}
            onClick={closeSidebar}
            isActive={location.pathname === '/gudang/notifications'}
          />

          {/* Papan Operasional */}
          <StandaloneMenuItem
            to="/gudang/papan-operasional"
            icon={Megaphone}
            label="Papan Operasional"
            onClick={closeSidebar}
            isActive={location.pathname.startsWith('/gudang/papan-operasional')}
          />

          {/* Pusat Info - Collapsible */}
          <MenuGroup
            icon={BookOpen}
            label="Pusat Info"
            badge={unreadAnnouncementsCount + appUpdatesCount}
            isExpanded={expandedGroups.pusatInfo}
            onToggle={() => toggleGroup('pusatInfo')}
            isActive={isPusatInfoActive}
          >
            <MenuItem
              to="/gudang/announcements"
              icon={Bell}
              label="Pengumuman"
              badge={unreadAnnouncementsCount}
              onClick={closeSidebar}
              isActive={isRouteActive('/gudang/announcements')}
            />
            <MenuItem
              to="/gudang/tutorial"
              icon={HelpCircle}
              label="Tutorial"
              onClick={closeSidebar}
              isActive={location.pathname === '/gudang/tutorial'}
            />
            <MenuItem
              to="/gudang/apa-yang-baru"
              icon={Sparkles}
              label="Apa yang Baru"
              badge={appUpdatesCount}
              onClick={closeSidebar}
              isActive={location.pathname === '/gudang/apa-yang-baru'}
            />
          </MenuGroup>

          {/* Pengiriman Barang */}
          <StandaloneMenuItem
            to="/gudang/pengiriman"
            icon={Truck}
            label="Pengiriman Barang"
            badge={pendingShipmentsCount}
            onClick={closeSidebar}
            isActive={isRouteActive('/gudang/pengiriman') || isRouteActive('/gudang/supply-requests')}
          />

          {/* Retur Barang */}
          <StandaloneMenuItem
            to="/gudang/retur"
            icon={RotateCcw}
            label="Retur Barang"
            badge={pendingReturnsCount}
            onClick={closeSidebar}
            isActive={isRouteActive('/gudang/retur') || isRouteActive('/gudang/logistic-returns')}
          />

          {/* Kartu Stok: Gudang Pusat dan nakes (Nov 2026) */}
          <StandaloneMenuItem
            to="/gudang/kartu-stok"
            icon={Package}
            label="Kartu Stok"
            onClick={closeSidebar}
            isActive={isRouteActive('/gudang/kartu-stok')}
          />

          {/* Stok Opname Nakes */}
          <StandaloneMenuItem
            to="/gudang/stok-opname"
            icon={ClipboardCheck}
            label="Stok Opname Nakes"
            onClick={closeSidebar}
            isActive={isRouteActive('/gudang/stok-opname')}
          />

          {/* Komplain Nakes (Okt 2026) */}
          <StandaloneMenuItem
            to="/gudang/komplain-nakes"
            icon={AlertTriangle}
            label="Komplain Nakes"
            onClick={closeSidebar}
            isActive={isRouteActive('/gudang/komplain-nakes')}
          />

          {/* Pemesanan Barang - Collapsible */}
          <MenuGroup
            icon={Package}
            label="Pemesanan Barang"
            isExpanded={expandedGroups.pemesanan}
            onToggle={() => toggleGroup('pemesanan')}
            isActive={isPemesananActive}
          >
            <MenuItem
              to="/gudang/purchase-orders"
              icon={ShoppingCart}
              label="Buat Surat Pesanan"
              onClick={closeSidebar}
              isActive={isRouteActive('/gudang/purchase-orders')}
            />
            <MenuItem
              to="/gudang/master-supplier"
              icon={Database}
              label="Master Supplier"
              onClick={closeSidebar}
              isActive={location.pathname === '/gudang/master-supplier'}
            />
            <MenuItem
              to="/gudang/master-vaxa-product"
              icon={Database}
              label="Master Produk Vaxa"
              onClick={closeSidebar}
              isActive={location.pathname === '/gudang/master-vaxa-product'}
            />
            <MenuItem
              to="/gudang/konfirmasi-produk"
              icon={Database}
              label="Konfirmasi Produk Baru"
              badge={productConfirmCount}
              onClick={closeSidebar}
              isActive={location.pathname === '/gudang/konfirmasi-produk'}
            />
            <MenuItem
              to="/gudang/master-supplier-product"
              icon={Database}
              label="Master Produk Supplier"
              onClick={closeSidebar}
              isActive={location.pathname === '/gudang/master-supplier-product'}
            />
            <MenuItem
              to="/gudang/stock-monitor/gudang"
              icon={Package}
              label="Stock Monitor Gudang"
              onClick={closeSidebar}
              isActive={location.pathname === '/gudang/stock-monitor/gudang'}
            />
            <MenuItem
              to="/gudang/stock-monitor/nakes"
              icon={Package}
              label="Stock Monitor Nakes"
              onClick={closeSidebar}
              isActive={location.pathname === '/gudang/stock-monitor/nakes'}
            />
          </MenuGroup>

          {/* Chat — Fix 90 (Feb 2026): submenu dihapus, langsung ke /gudang/chat.
              Filter per tipe (Semua/Pengiriman/Retur/Nakes/Koordinasi) tetap
              tersedia sebagai chip di dalam halaman /gudang/chat.
          */}
          <StandaloneMenuItem
            to="/gudang/chat"
            icon={MessageCircle}
            label="Chat"
            badge={chatBadgeTotal || unreadChatCount}
            onClick={closeSidebar}
            isActive={isChatActive}
          />
        </nav>

        {/* Bottom: Profile row (with bell right) + Logout row */}
        <div className="flex-shrink-0 p-2 border-t border-gray-200 bg-white" style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}>
          <div className="flex items-center gap-2 mb-2">
            <Link
              to="/gudang/profile"
              onClick={closeSidebar}
              className="flex items-center gap-2 flex-1 min-w-0 px-2 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              data-testid="profile-link"
            >
              <User className="w-5 h-5 text-gray-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate leading-tight">{user?.name}</p>
                <p className="text-[10px] text-gray-500 truncate leading-tight">{user?.user_id} · Gudang</p>
              </div>
            </Link>

            <Link
              to="/gudang/notifications"
              onClick={closeSidebar}
              aria-label="Notifikasi"
              className="relative flex items-center justify-center w-11 h-11 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
              data-testid="sidebar-notification-bell"
            >
              <Bell className="w-5 h-5 text-gray-600" />
              {unreadNotificationsCount > 0 && (
                <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] font-bold min-w-[16px] h-4 flex items-center justify-center px-1 rounded-full">
                  {unreadNotificationsCount > 9 ? '9+' : unreadNotificationsCount}
                </span>
              )}
            </Link>
          </div>

          <Button
            onClick={handleLogout}
            data-testid="logout-button"
            variant="outline"
            size="sm"
            className="w-full justify-center text-red-600 border-red-200 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content - Scrollable area, offset by sidebar width on desktop */}
      <div className="flex-1 min-w-0 min-h-screen lg:ml-64 lg:h-screen lg:overflow-y-auto">
        {/* Mobile Sticky Header — Feb 2026 (v2 redesign) */}
        <MobileStickyHeader
          userName={user?.name || 'User'}
          userRole="Gudang"
          sidebarOpen={sidebarOpen}
          onMenuClick={() => setSidebarOpen(!sidebarOpen)}
        />
        <div className="w-full h-full overflow-y-auto pt-14 lg:pt-0 pb-24">
          <PullToRefresh>
            <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-6">
              <Outlet />
            </div>
          </PullToRefresh>
        </div>
      </div>
      
      {/* Pop up perubahan alamat pengiriman terjadwal (khusus gudang) */}
      {user?.role === "gudang" && <AddressChangePopup />}
    </div>
  );
}
